import { useState, createContext } from "react";
import { useDispatch } from "react-redux";

import {
  setLoadingFalseAction,
  setLoadingTrueAction,
} from "../../../redux/actions/loadingActions/loadingActions";
import {
  toastErrorMessage,
  toastSuccessMessage,
} from "../../../utils/toastMessage/toastMessage";
import {
  getDebtorsAdminService,
  createDebtorAdminService,
  deleteDebtorAdminService,
  editDebtorAdminService,
  getUsersAdminService,
} from "../../../services/adminServices";
import { userRoleItems } from "../../../utils/enum";

export const debtorAdminContext = createContext({
  debtors: [],
  setDebtors: () => {},
  id: "",
  setId: () => {},
  sort: "",
  setSort: () => {},
  start: "",
  setStart: () => {},
  end: "",
  setEnd: () => {},
  pageCount: null,
  setPageCount: () => {},
  pageNumber: null,
  setPageNumber: () => {},
  itemsCount: null,
  setItemsCount: () => {},
  isShowModal: null,
  setIsShowModal: () => {},
  handleShowModal: () => {},
  handleHideModal: () => {},
  handleGetUsers: () => {},
  handleGetDebtors: () => {},
  handleCreateDebtor: () => {},
  handleEditDebtor: () => {},
  handleDeleteDebtor: () => {},
});

const DebtorAdminContext = ({ children }) => {
  const dispatch = useDispatch();

  const [debtors, setDebtors] = useState([]);
  const [id, setId] = useState("");
  const [sort, setSort] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [pageCount, setPageCount] = useState(1);
  const [pageNumber, setPageNumber] = useState(1);
  const [itemsCount, setItemsCount] = useState(100);
  const [isShowModal, setIsShowModal] = useState(false);

  // Modal Operation
  const handleShowModal = (val, setValue) => {
    setIsShowModal(true);

    if (val) {
      setId(val._id);
      setValue("amount", val.amount);
      setValue("description", val.description);
      setValue("dueDate", val.dueDate);
      setValue("user", {
        value: val?.user?._id,
        label: `${val?.user?.firstName} ${val?.user?.lastName} - ${val?.user?.phoneNumber}`,
      });
    }
  };
  const handleHideModal = () => {
    setIsShowModal(false);
    setId("");
  };

  // Get Users
  const handleGetUsers = async (filter, callback) => {
    const objData = {
      filter,
      pageNumber: 1,
      itemsCount: 200,
      role: userRoleItems[1],
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getUsersAdminService(objData);
      if (status === 200) {
        callback &&
          callback(
            data.result?.users?.map((item) => ({
              value: item._id,
              label: `${item.firstName} - ${item.lastName} - ${item.phoneNumber}`,
              data: item,
            }))
          );
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Get Debtors
  const handleGetDebtors = async (event) => {
    event?.preventDefault();

    const objData = {
      ...(start && { start }),
      ...(end && { end }),
      pageNumber,
      sort,
      itemsCount,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await getDebtorsAdminService(objData);
      if (status === 200) {
        setDebtors(data.result?.debtors);
        setPageCount(data?.result?.allPagesCount);
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
    } finally {
      dispatch(setLoadingFalseAction());
    }
  };

  // Create Debtor
  const handleCreateDebtor = async (userData) => {
    const objData = {
      ...userData,
      user: userData?.user?.value,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await createDebtorAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetDebtors();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Edit Debtor
  const handleEditDebtor = async (userData) => {
    const objData = {
      ...userData,
      id,
      user: userData?.user?.value,
    };
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await editDebtorAdminService(objData);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetDebtors();
        handleHideModal();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  // Delete Debtor
  const handleDeleteDebtor = async (id) => {
    try {
      dispatch(setLoadingTrueAction());
      const { status, data } = await deleteDebtorAdminService(id);
      if (status === 200) {
        toastSuccessMessage(data.resultMessage);
        handleGetDebtors();
      }
    } catch ({ response }) {
      if (response) {
        if (response.status) {
          toastErrorMessage(response.data.resultMessage);
        }
      }
      dispatch(setLoadingFalseAction());
    }
  };

  return (
    <debtorAdminContext.Provider
      value={{
        debtors,
        setDebtors,
        id,
        setId,
        sort,
        setSort,
        start,
        setStart,
        end,
        setEnd,
        pageCount,
        setPageCount,
        pageNumber,
        setPageNumber,
        itemsCount,
        setItemsCount,
        isShowModal,
        setIsShowModal,
        handleShowModal,
        handleHideModal,
        handleGetUsers,
        handleGetDebtors,
        handleCreateDebtor,
        handleEditDebtor,
        handleDeleteDebtor,
      }}
    >
      {children}
    </debtorAdminContext.Provider>
  );
};

export default DebtorAdminContext;
